import React from "react";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import AdvancedTable from "../../components/AdvancedTable.js";
import {
  StringComboFilter,
  NumberComboFilter,
  SelectMultipleColumnsFilter,
} from "../../components/filterTypes/filterTypes.js";
import {
  apiGetCurrency,
  apiGetCountry,
  apiDownloadFile,
  apiGetFcsDepositWithdrawData,
  apiGetFcsDepositWithdrawDownloadData,
  apiGetFcsDepositWithdrawSummary,
  apiGetCurrencyPrice
} from "../../resource/index.js";
import Storage from "../../common/lib/storage.js";
import DateRange from "../../common/lib/dateRange.js";
import * as types from '../../constants/actionTypes.js';
import {
  FUND_TYPES,
  DEFAULT_FCS_DEPOSIT_WITHDRAW_STATE,
} from '../../constants/constants.js';
import ControlledMap from "../../components/geo/ControlledMap.js";
import FcsDepositWithdrawSummary from "./FcsDepositWithdrawSummary.js";
import useAPIError from "../../common/hooks/useAPIError.js";
import useSearchSYN from "../../common/hooks/useSearchSYN.js"
import {NumberFormatter} from "../../util/tableHelper.js";
import theme from '../../constants/theme.js';
import { XSSRecover } from "../../util/xssHelper.js";

const useStyles = makeStyles(() => ({
  root: {
    width: (dynamicWidth) => dynamicWidth - theme.spacing(39).replace('px', ''),
  },
  topDiv: {
    display: 'flex',
    height: "500px",
    position: 'relative',
    width: (dynamicWidth) => dynamicWidth - theme.spacing(40).replace('px', ''),
  },
}));

const find = (arr, key, value) => {
  for (let item of arr) {
    if (item[key] === value) {
      return item;
    }
  }
  return null;
}

export default function FcsDepositWithdrawReport() {
  const { fcsDepositWithdrawState } = useSelector((state) => state.report);
  const dispatch = useDispatch();
  const setCountry = (val) => dispatch({ type: types.SET_COUNTRY_LIST, value: val});
  const setSelectedCountry = (val) => dispatch({ type: types.SET_SELECTED_COUNTRIES, value: val});
  const setFcsDepositWithdrawState = (val) => dispatch({ type: types.SET_FCS_DEPOSIT_WITHDRAW_STATE, value: val });
  const setSelectedContinents = (initState) => dispatch({ type: types.SET_CONTINENTS_LIST, value: initState });
  const setUserAndLevelName = (val) => dispatch({ type: types.SET_USER_AND_LEVEL_NAME, value: val });
  const setLevelId = (val) => dispatch({ type: types.SET_LEVEL_ID, value: val });
  const setShowAll = (val) => dispatch({ type: types.SET_SHOW_ALL, value: val });
  const { addError } = useAPIError();
  const [currency, setCurrency] = React.useState([]);
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [noData, setNoData] = React.useState(true);
  const [count, setCount] = React.useState(0);
  const [pageCount, setPageCount] = React.useState(0);
  const [selectedSort, setSelectedSort] = React.useState({});
  const [selectedFilter, setSelectedFilter] = React.useState({});
  const [checkedUsd, setCheckedUsd] = React.useState(false);
  const [summary, setSummary] = React.useState([]);
  const [prices, setPrices] = React.useState({});
  const [click, setClick] = React.useState('');
  const regulator = Storage.getRegulator() ?? "";
  const { filters, countryList } = useSelector(state => state.report);
  const { toggleSearchSYN } = useSearchSYN();

  const columns = React.useMemo(
    () =>
      [
        {
          Header: "License",
          accessor: "license",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Ticket ID",
          accessor: "ticket_id",
          disableSortBy: false,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Agent",
          accessor: "ib_name",
          disableSortBy: false,
          disableFilters: false,
          Filter: StringComboFilter,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            XSSRecover(cell.value, "left")
          ),
        },
        {
          Header: "Client Name",
          accessor: "user_name",
          disableSortBy: true,
          disableFilters: false,
          Filter: StringComboFilter,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            XSSRecover(cell.value, "left")
          ),
        },
        {
          Header: "CPA",
          accessor: "cpa",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "AFFID",
          accessor: "affid",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Client Type",
          accessor: "client_type",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Account",
          accessor: "account",
          disableSortBy: true,
          disableFilters: false,
          Filter: NumberComboFilter,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Currency",
          accessor: "currency",
          disableSortBy: false,
          Filter: ({ column }) =>
            SelectMultipleColumnsFilter({ column }, { dropdown: currency }),
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Master Order No",
          accessor: "master_order_no",
          disableSortBy: true,
          disableFilters: false,
          Filter: StringComboFilter,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value || "-"}</div>
          ),
        },
        {
          Header: "Sub Order No",
          accessor: "order_number",
          disableSortBy: true,
          disableFilters: false,
          Filter: StringComboFilter,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value || "-"}</div>
          ),
        },
        {
          Header: "Deposit",
          accessor: "depositCount",
          id: 'actual_amount',
          disableSortBy: false,
          disableFilters: true,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Withdraw",
          accessor: "withdrawCount",
          id: 'withdrawCount',
          disableSortBy: false,
          disableFilters: true,
          alwaysVisible: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Open Time",
          accessor: "create_time",
          disableSortBy: true,
          disableFilters: true,
          show: false,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Processed Time",
          accessor: "accepted_time",
          disableSortBy: true,
          disableFilters: true,
          show: false,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Last Update Time",
          accessor: "update_time",
          disableSortBy: true,
          disableFilters: true,
          show: false,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Type",
          accessor: "payment_type_text",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "PSP Name",
          accessor: "psp_name",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            XSSRecover(cell.value, "left")
          ),
        },
        {
          Header: "Status",
          accessor: "status",
          disableSortBy: false,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Fund Status",
          accessor: "fund_status",
          disableSortBy: false,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Processed Notes",
          accessor: "processed_notes",
          disableSortBy: true,
          disableFilters: true,
          show: false,
          Cell: ({ row, cell }) => (
            XSSRecover(cell.value, "left")
          ),
        },
        {
          Header: "Country",
          accessor: "country",
          disableSortBy: true,
          disableFilters: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Reversal Time",
          accessor: "reversal_time",
          disableSortBy: false,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Reversed Type",
          accessor: "reversal_type",
          disableSortBy: false,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Internal Notes",
          accessor: "internal_notes",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            XSSRecover(cell.value, "left")
          ),
        },
        {
          Header: "Withdraw(Apply)",
          accessor: "withdraw_apply",
          disableSortBy: true,
          disableFilters: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Status(Apply)",
          accessor: "status_apply",
          disableSortBy: true,
          disableFilters: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Callback Amount",
          accessor: "callback_amount",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Adjusted Amount",
          accessor: "adjusted_amount",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "User Visible Amount",
          accessor: "visible_amount",
          disableSortBy: true,
          disableFilters: true,
          show: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Id(Apply)",
          accessor: "withdraw_id",
          disableSortBy: true,
          disableFilters: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Server",
          accessor: "server",
          disableSortBy: true,
          disableFilters: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Regulator",
          accessor: "regulator",
          disableSortBy: true,
          disableFilters: true,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
        {
          Header: "Channel Decline Reason",
          accessor: "channel_decline_reason",
          disableSortBy: true,
          disableFilters: true,
          show: false,
          Cell: ({ row, cell }) => (
            <div style={{ textAlign: "left" }}>{cell.value}</div>
          ),
        },
      ], [currency]);

  const getPayments = () =>{
    const payments=[]
    if (filters.withdraw) {
      payments.push({
        dataType: filters.withdrawDataType || undefined,
        paymentStatus: filters.withdrawPaymentStatus ? filters.withdrawPaymentStatus.map(r => r.value) : undefined || undefined,
        paymentType: filters.withdrawPaymentMethod || undefined,
        paymentChannel: filters.withdrawPaymentChannel || undefined,
      });
    };

    if (filters.deposit) {
      payments.push({
        dataType: filters.depositDataType || undefined,
        paymentStatus: filters.depositPaymentStatus ? filters.depositPaymentStatus.map(r => r.value) : undefined || undefined,
        paymentType: filters.depositPaymentMethod || undefined,
        paymentChannel: filters.depositPaymentChannel || undefined,
      });
    };
    return payments;
  };

  const downloadFile = (file) => {
    apiDownloadFile(file)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([
          new Uint8Array([0xEF, 0xBB, 0xBF]),
          "",
          res.data],
          { type: "text/plain;charset=utf-8" }
          ));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
  };

  const getDownloadData = () => {
    setLoading(true);
    apiGetFcsDepositWithdrawDownloadData({
      regulator: regulator,
      sort: selectedSort,
      filter: selectedFilter ? selectedFilter : {},
      from: filters.fromDate || "",
      to: filters.toDate || "",
      countryCodes: filters.countryCodes || undefined,
      levelId: filters.levelId || "",
      showAll: filters.showAll,
      payments: getPayments(),
    })
      .then((res) => {
        setLoading(false);
        if (res.status === 200) {
          downloadFile(res.data.data.file);
        }
      })
      .catch(() => {
        setLoading(false);
      })
  };

  const getData = React.useCallback(async () => {
    try {
      const currency_res = (await apiGetCurrency(regulator)).data;
      setCurrency(currency_res.data.currency);

      const country_res = (await apiGetCountry(regulator)).data;
      setCountry(country_res.data.country);

      const currencyList = currency_res.data.currency.map(r => r.value).join(',');
      const price_res = (await apiGetCurrencyPrice(regulator, currencyList)).data;
      const currency_prices = {};
      price_res.data.price.forEach(r => {
        const key = r.symbol.replace('USD', '');
        if (r.symbol.indexOf('USD') === 0) {
          currency_prices[key] = 1 / r.price;
        } else {
          currency_prices[key] = r.price;
        }
      })
      setPrices(currency_prices);
    } catch (err) {
      console.log(err);
      setCurrency([]);
      setCountry([]);
      setPrices({});
    }
  }, [regulator]);

  React.useEffect(() => {
    if (regulator) {
        getData();
    }
  }, [regulator]);

  React.useEffect(() => {
    if (click !== '') {
      toggleSearchSYN();
    }
  }, [click]);

  let geoFund = {};
  let totalSummary = {};
  summary.forEach(r => {
    if (r.country_iso && r.currency) {
      geoFund[r.country_iso] = geoFund[r.country_iso] || {};
      geoFund[r.country_iso][r.currency] = geoFund[r.country_iso][r.currency] || {};
      geoFund[r.country_iso][r.currency].deposit =  geoFund[r.country_iso][r.currency].deposit || 0;
      geoFund[r.country_iso][r.currency].withdraw = geoFund[r.country_iso][r.currency].withdraw || 0;
      geoFund[r.country_iso][r.currency].withdraw_apply = geoFund[r.country_iso][r.currency].withdraw_apply || 0;

      totalSummary[r.currency] = totalSummary[r.currency] || {};
      totalSummary[r.currency].deposit = totalSummary[r.currency].deposit || 0;
      totalSummary[r.currency].withdraw = totalSummary[r.currency].withdraw || 0;
      totalSummary[r.currency].withdraw_apply = totalSummary[r.currency].withdraw_apply || 0;
      totalSummary[r.currency].decimal = totalSummary[r.currency].decimal || 0;

      if (r.dataType === FUND_TYPES.DEPOSIT) {
        geoFund[r.country_iso][r.currency].deposit += r.sumAmount;
        totalSummary[r.currency].deposit += r.sumAmount;
      } else if (r.dataType === FUND_TYPES.WITHDRAW) {
        geoFund[r.country_iso][r.currency].withdraw -= r.sumAmount;
        geoFund[r.country_iso][r.currency].withdraw_apply -= r.apply_amount;
        totalSummary[r.currency].withdraw -= r.sumAmount;
        totalSummary[r.currency].withdraw_apply -= r.apply_amount;
      }

      if (totalSummary[r.currency].decimal < r.decimal) {
        totalSummary[r.currency].decimal = r.decimal;
      }
    }
  })

  if (checkedUsd) {
    const usdFund = {};
    Object.keys(geoFund).forEach(country_iso => {
      const c = geoFund[country_iso];
      usdFund[country_iso] = usdFund[country_iso] || { USD: { deposit: 0, withdraw: 0, withdraw_apply: 0 } };
      Object.keys(c).forEach(currency => {
        if (currency !== 'USD') {
          const rate = prices[currency];
          if (rate === undefined || rate === null || Number.isNaN(rate) || rate <= 0) return;
          usdFund[country_iso].USD.deposit += c[currency].deposit * rate;
          usdFund[country_iso].USD.withdraw += c[currency].withdraw * rate;
          usdFund[country_iso].USD.withdraw_apply += c[currency].withdraw_apply * rate;
        } else {
          usdFund[country_iso].USD.deposit += c[currency].deposit;
          usdFund[country_iso].USD.withdraw += c[currency].withdraw;
          usdFund[country_iso].USD.withdraw_apply += c[currency].withdraw_apply;
        }
      })
    });
    geoFund = usdFund;

    const usdSummary = { USD: { deposit: 0, withdraw: 0, withdraw_apply: 0 } };
    Object.keys(totalSummary).forEach(currency => {
      if (currency !== 'USD') {
        const rate = prices[currency];
        if (rate === undefined || rate === null || Number.isNaN(rate) || rate <= 0) return;
        usdSummary.USD.deposit += totalSummary[currency].deposit * rate;
        usdSummary.USD.withdraw += totalSummary[currency].withdraw * rate;
        usdSummary.USD.withdraw_apply += totalSummary[currency].withdraw_apply * rate;
      } else {
        usdSummary.USD.deposit += totalSummary[currency].deposit;
        usdSummary.USD.withdraw += totalSummary[currency].withdraw;
        usdSummary.USD.withdraw_apply += totalSummary[currency].withdraw_apply;
      }
    });
    totalSummary = usdSummary
  }

  const fetchData = ({ pageSize, pageIndex, sortParams, filterParams }) => {
    if (DateRange.FindRangeError(filters.fromDate, filters.toDate)) {
      addError(DateRange.FindRangeError(filters.fromDate, filters.toDate));
      return;
    }

    const filterOptions = {
      currency: filterParams.currency || undefined,
      agent: filterParams.ib_name || undefined,
      clientName: filterParams.user_name || undefined,
      account: filterParams.account && filterParams.account["="] ? filterParams.account["="] : (filterParams.account || undefined),
      orderNumber: filterParams.master_order_no || undefined,
      subOrderNumber: filterParams.order_number || undefined,
    }
    setSelectedSort(sortParams);
    setSelectedFilter(filterOptions);
    setLoading(true);
    setNoData(false);

    apiGetFcsDepositWithdrawData({
      regulator: regulator,
      pageNo: pageIndex + 1,
      pageSize: pageSize,
      sort: sortParams,
      filter: filterOptions ? filterOptions : {},
      from: filters.fromDate,
      to: filters.toDate,
      countryCodes: filters.countryCodes || undefined,
      levelId: filters.levelId || "",
      showAll: filters.showAll,
      payments: getPayments(),
    })
      .then((res) => {
        if (res.data.status === "success") {
          setData(res.data.data.fcs_dw);
          setCount(res.data.data.total);
          setPageCount(Math.ceil(res.data.data.total / pageSize));
        }else{
          console.log("[Get FCS D&W] status " + res.data.status);
          setNoData(true);
          setData([]);
        }
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
        setNoData(true);
        setData([]);
      })

    apiGetFcsDepositWithdrawSummary({
      regulator: regulator,
      filter: filterOptions ? filterOptions : {},
      from: filters.fromDate,
      to: filters.toDate,
      countryCodes: filters.countryCodes || undefined,
      levelId: filters.levelId || "",
      showAll: filters.showAll,
      payments: getPayments(),
    })
      .then((res) => {
        if (res.data.status === "success") {
          setSummary(res.data.data.summary);
        }
      })
      .catch((e) => {
        console.log(e);
        setSummary([]);
      })
  }

  const options = {};

  if (checkedUsd) {
    options.value=(node) => {
      if (checkedUsd && node && node.value && node.value.USD) {
        return node.value.USD.deposit + node.value.USD.withdraw;
      } else {
        return node;
      }
    };
    options.legends=[{
        anchor: 'bottom-left',
        direction: 'column',
        justify: true,
        translateX: 10,
        translateY: -5,
        itemsSpacing: 0,
        itemWidth: 94,
        itemHeight: 18,
        itemDirection: 'left-to-right',
        itemTextColor: '#444444',
        itemOpacity: 0.85,
        symbolSize: 18,
        effects: [{
          on: 'hover',
          style: {
            itemTextColor: '#000000',
            itemOpacity: 1
          }
        }]
    }];
  }
  const [dynamicWidth, setDynamicWidth] = React.useState(window.innerWidth);
  const classes = useStyles(dynamicWidth);

  function handleResize() {
    setDynamicWidth(window.innerWidth)
  }
  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resetFilter = () => {
    setSelectedCountry([]);
    setSelectedContinents([]);
    setFcsDepositWithdrawState(DEFAULT_FCS_DEPOSIT_WITHDRAW_STATE);
    setUserAndLevelName("");
    setLevelId("");
    setShowAll(true);
  }

  return (
    <div className={classes.root}>
      <div className={classes.topDiv}>
        <ControlledMap
          data={geoFund}
          Tooltip={FcsDepositWithdrawSummary}
          options={options}
          onClick={(feature) => {
            const item = find(countryList, "iso", feature.id);
            if (item && item.value) {
              setSelectedCountry([item]);
              setClick(!click);
            }
          }}
        />
        <FcsDepositWithdrawSummary title="Total" data={totalSummary}/>
        <FormControlLabel
            style={{ position: 'absolute', left: 10, top: 0 }}
            control={<Switch
                      checked={checkedUsd}
                      onChange={(e) => setCheckedUsd(e.target.checked)}
                      color="primary"
                      name="checkedUsd"
                    />}
            label="Amounts in USD"
          />
      </div>
      <AdvancedTable
        columns={columns}
        data={data}
        fetchData={fetchData}
        loading={loading}
        noData={noData}
        count={count}
        pageCount={pageCount}
        getDownloadData={getDownloadData}
        resetFilter={resetFilter}
      />
    </div>
  );
}
